import { useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, Upload, FileText, Download, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roles = [
  { id: "contractor", label: "Contractor / Builder", icon: "🏗️", description: "OSHA logs, bids, change orders" },
  { id: "tech_writer", label: "Technical Writer", icon: "✍️", description: "DITA, S1000D, documentation" },
  { id: "defense", label: "Defense / Aerospace", icon: "✈️", description: "MIL-STD, S1000D compliance" },
  { id: "healthcare", label: "Healthcare", icon: "🏥", description: "HIPAA, FHIR, HL7 standards" }
];

export default function OnboardingFlow({ user, onComplete }) {
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [processedData, setProcessedData] = useState(null);

  const handleRoleSelect = async (role) => {
    setSelectedRole(role);
    
    // Save role to backend
    try {
      const token = localStorage.getItem("standari_token");
      await axios.post(
        `${API}/onboarding/complete-step`,
        { step: "role_selected", value: role.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTimeout(() => setStep(2), 500);
    } catch (error) {
      console.error("Failed to save role:", error);
      setStep(2); // Continue anyway
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleUploadAndProcess = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem("standari_token");
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setDocumentId(uploadResponse.data.document_id);

      // Mark upload complete
      await axios.post(
        `${API}/onboarding/complete-step`,
        { step: "first_upload", value: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUploading(false);
      setProcessing(true);

      // Auto-process
      const processResponse = await axios.post(
        `${API}/documents/${uploadResponse.data.document_id}/process`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProcessedData(processResponse.data);
      setProcessed(true);
      setProcessing(false);
      
      toast.success("Document processed! Ready to export.");
      setTimeout(() => setStep(3), 1000);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Processing failed");
      setUploading(false);
      setProcessing(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("standari_token");

      const response = await axios.post(
        `${API}/export-v2`,
        {
          content_id: processedData.processed_content_id,
          format: "dita",
          output_format: "pdf",
          options: {}
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `export_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Mark export complete
      await axios.post(
        `${API}/onboarding/complete-step`,
        { step: "first_export", value: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Success! You just saved 2 hours.");
      
      // Complete onboarding
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 2000);

    } catch (error) {
      toast.error("Export failed");
    }
  };

  const progressPercent = (step / 3) * 100;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900">Welcome to Standari!</h2>
              <p className="text-slate-600">Let's get you started in 90 seconds</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onComplete && onComplete()}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Step {step} of 3</span>
              <span className="text-sm text-slate-500">{Math.round(progressPercent)}% complete</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Step 1: Role Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">What describes you best?</h3>
                <p className="text-slate-600">We'll personalize your experience</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSelect(role)}
                    className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="text-3xl mb-2">{role.icon}</div>
                    <div className="font-semibold text-slate-900">{role.label}</div>
                    <div className="text-sm text-slate-600">{role.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Upload */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Upload your first document</h3>
                <p className="text-slate-600">We'll clean and structure it automatically</p>
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-all"
              >
                <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                {file ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-slate-900">{file.name}</span>
                    </div>
                    <Button
                      onClick={handleUploadAndProcess}
                      disabled={uploading || processing}
                      className="btn-primary"
                    >
                      {uploading ? "Uploading..." : processing ? "Processing..." : "Process Document"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-slate-600 mb-4">Drop your document here</p>
                    <p className="text-sm text-slate-500 mb-4">PDF, Word, PowerPoint, up to 50MB</p>
                    <input
                      type="file"
                      id="onboarding-file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.ppt,.pptx"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById("onboarding-file").click()}
                    >
                      Browse Files
                    </Button>
                  </div>
                )}
              </div>

              {processing && (
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-blue-800">Analyzing document structure...</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Export */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Document ready!</h3>
                <p className="text-slate-600">We cleaned and structured your document. Export now?</p>
              </div>

              {processedData?.preview && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">What we found:</h4>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>✓ Document structure analyzed</li>
                    <li>✓ Compliance keywords identified</li>
                    <li>✓ Standards mapping complete</li>
                    {processedData.preview.glossary_count > 0 && (
                      <li>✓ {processedData.preview.glossary_count} glossary terms extracted</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex gap-4">
                <Button onClick={handleExport} className="btn-primary flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Export to PDF
                </Button>
                <Button variant="outline" onClick={() => onComplete && onComplete()}>
                  Skip for Now
                </Button>
              </div>

              <div className="bg-green-50 p-4 rounded-lg text-center">
                <p className="text-sm font-medium text-green-900">
                  🎉 Success! You just saved 2 hours of manual formatting work.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
